import { clamp, toSafeInteger } from "es-toolkit/compat";
import { z } from "zod";
import { isUnsafeInteger, isUnsafeNumber } from "./precision.ts";

export const IntegerValueSchema = z.number().int();
export const NumberValueSchema = z.number();

/**
 * parse a string or number as a number. a number is an integer or a floating point value.
 * @returns number is successfully parsed, null if invalid
 */
export function parseInteger(
  value: unknown,
  {
    rounding = "round",
    shouldBeSafe = true,
  }: {
    rounding?: "floor" | "ceil" | "round";
    shouldBeSafe?: boolean;
  }
): number {
  if (typeof value !== "string" && typeof value !== "number") return NaN;

  const num = Number(value);

  if (isNaN(num)) {
    return NaN;
  }

  if (num > Number.MAX_SAFE_INTEGER || num < Number.MIN_SAFE_INTEGER) {
    return shouldBeSafe ? toSafeInteger(num) : num;
  }

  return rounding === "floor"
    ? Math.floor(num)
    : rounding === "ceil"
      ? Math.ceil(num)
      : Math.round(num);
}

/**
 * parse a string or number as a number. a number is an integer or a floating point value.
 * @returns number if parsed successfully, null if invalid
 */
export function parseNumber(
  value: unknown,
  { shouldBeSafe = true }: { shouldBeSafe?: boolean }
): number {
  if (typeof value !== "string" && typeof value !== "number") return NaN;

  if (isNaN(Number(value))) {
    return NaN;
  }

  return Number.isInteger(value)
    ? shouldBeSafe && isUnsafeInteger(value)
      ? typeof value === "string"
        ? parseFloat(value)
        : value // change to float when integer is unsafe
      : parseInteger(value, {
          shouldBeSafe: false,
          rounding: "round", // not important, since value is already integer
        })
    : shouldBeSafe && isUnsafeNumber(value)
      ? clamp(
          typeof value === "string" ? parseFloat(value) : value,
          Number.MIN_VALUE,
          Number.MAX_VALUE
        )
      : typeof value === "string"
        ? parseFloat(value)
        : value;
}
